// Lo que las aplicaciones usan de este paquete. Todo lo demás es interno.
//
// ## Qué es esto y qué no
//
// Es el **contrato** con la API, tipado y generado. No es una capa de datos: no
// guarda nada, no reintenta, no cachea. Cuando haga falta eso —y va a hacer
// falta— vive en la aplicación o en un paquete aparte, no acá: un cliente que
// además decide cuándo volver a pedir es un cliente que nadie puede leer.
//
// ## Los tipos no se escriben, se generan
//
// `esquema.ts` lo escribe openapi-typescript a partir de `openapi/v1.json`, que
// a su vez sale de compilar la API. Editarlo a mano es trabajo que se pierde en
// la siguiente regeneración. Si un tipo está mal, está mal en el backend.
//
//   pnpm --filter @tallerya/api-client generar
//
// El pipeline regenera y **falla si el resultado difiere de lo que está en el
// repo**. Ese es el punto de F-4: cambiar un DTO y no regenerar se nota al
// compilar, no en producción.

import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './esquema';

export type { components, operations, paths } from './esquema';

/// Lo que necesita saber el cliente para hablar con la API.
export interface OpcionesCliente {
  /// La raíz de la API, sin barra final. Sale de la configuración de cada app,
  /// porque no es la misma en local, en dev y en producción.
  baseUrl: string;

  /// De dónde sale el token. Se pide **en cada petición** y no una sola vez al
  /// crear el cliente, porque el token se renueva: guardarlo acá significaría
  /// mandar uno vencido en cuanto la sesión pase de la primera hora.
  ///
  /// Devolver `undefined` manda la petición sin credenciales, que es lo
  /// correcto mientras nadie ha entrado todavía.
  obtenerToken?: () => string | undefined | Promise<string | undefined>;

  /// En qué taller se está trabajando, para quien está en varios (#205). Va en
  /// la cabecera `X-Tenant-Id` de cada petición.
  ///
  /// **No define el taller, elige.** La API solo acepta uno de los que el token
  /// trae firmados en `tenant_ids`; uno que no esté ahí es una petición sin
  /// taller, y responde 403. Se pide en cada petición por lo mismo que el token:
  /// la persona puede cambiar de taller sin recrear el cliente.
  ///
  /// `undefined` no manda la cabecera, y entonces la API usa el único taller del
  /// token si hay uno solo —el caso del mecánico—.
  obtenerTaller?: () => string | undefined;
}

/// El nombre de la cabecera, el mismo que lee `TokenTenants` en la API.
export const CABECERA_TALLER = 'X-Tenant-Id';

/// Crea el cliente tipado de la API.
///
/// Los tipos salen del contrato: pedir una ruta que no existe, mandar un campo
/// que el backend no declara o leer uno que no devuelve **no compila**.
export function crearCliente({ baseUrl, obtenerToken, obtenerTaller }: OpcionesCliente) {
  const cliente = createClient<paths>({ baseUrl });

  if (obtenerToken) {
    const autenticacion: Middleware = {
      async onRequest({ request }) {
        const token = await obtenerToken();

        // Sin token se manda la petición igual y la API responde 401. Es lo que
        // se quiere: el frontend no decide quién está autorizado —la
        // comprobación real vive en el servidor— y un 401 explícito se puede
        // manejar; una petición que nunca sale, no.
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }

        return request;
      },
    };

    cliente.use(autenticacion);
  }

  if (obtenerTaller) {
    const taller: Middleware = {
      onRequest({ request }) {
        const elegido = obtenerTaller();

        if (elegido) {
          request.headers.set(CABECERA_TALLER, elegido);
        }

        return request;
      },
    };

    cliente.use(taller);
  }

  return cliente;
}

/// El tipo del cliente ya creado, para quien necesite pasarlo o guardarlo.
export type ClienteApi = ReturnType<typeof crearCliente>;
